/**
 * Fila de casos — uma linha por problema, largura inteira da mesa.
 */
import React, { useState } from 'react';
import { AlertTriangle, ChevronDown, ChevronRight } from 'lucide-react';
import type { EnrichedProblemIssue } from '../../services/problemDashboardData';
import { ISSUE_SEVERITY_LABELS, ISSUE_TYPE_LABELS } from '../../types/issue';
import { resolveIssueAssigneeName } from '../../utils/issueAssignee';
import { getIssueNextAction } from '../../utils/issueNextAction';
import {
  computeIssueProgressLight,
  formatIssueStageLabel,
  getMaturityLevel,
  issueHasResolutionMaturity,
} from '../../utils/issueProgress';
import { getIssueSlaDays } from '../../utils/issueSla';
import type { OrgMember } from '../../hooks/useOrgMembers';

const SEV_BAR: Record<string, string> = {
  critical: 'bg-red-600',
  high: 'bg-orange-500',
  medium: 'bg-amber-400',
  low: 'bg-slate-300 dark:bg-slate-600',
};

const ROW_GRID = 'grid w-full grid-cols-[minmax(0,2.3fr)_minmax(0,0.72fr)_minmax(0,0.82fr)_minmax(0,0.78fr)_minmax(0,0.9fr)_72px] items-center gap-x-3';

interface ProblemCaseListProps {
  issues: EnrichedProblemIssue[];
  selectedId: string | null;
  orgMembers: OrgMember[];
  isLoading?: boolean;
  emptyTitle?: string;
  emptySubtitle?: string;
  onOpenWorkspace: (issue: EnrichedProblemIssue) => void;
}

export const ProblemCaseList: React.FC<ProblemCaseListProps> = ({
  issues,
  selectedId,
  orgMembers,
  isLoading,
  emptyTitle = 'Nenhum problema registrado',
  emptySubtitle,
  onOpenWorkspace,
}) => {
  const [closedOpen, setClosedOpen] = useState(false);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16 text-[12px] text-slate-400">
        Carregando casos...
      </div>
    );
  }

  if (issues.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center px-6 py-16 text-center">
        <div className="mb-3 rounded-full border border-slate-200 bg-slate-50 p-3 dark:border-slate-700 dark:bg-slate-800">
          <AlertTriangle size={20} className="text-slate-400" />
        </div>
        <p className="text-[13px] font-semibold text-slate-700 dark:text-slate-200">{emptyTitle}</p>
        {emptySubtitle && <p className="mt-1 max-w-sm text-[11px] text-slate-500">{emptySubtitle}</p>}
      </div>
    );
  }

  const sorted = [...issues].sort((a, b) => b.priorityScore - a.priorityScore);
  const regular = sorted.filter(i => i.status !== 'closed' && i.status !== 'cancelled');
  const closed = sorted.filter(i => i.status === 'closed' || i.status === 'cancelled');

  return (
    <div className="w-full">
      <div className={`${ROW_GRID} sticky top-0 z-10 border-b border-slate-200 bg-slate-50 px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wide text-slate-500 dark:border-slate-700 dark:bg-slate-900`}>
        <span>Caso · {regular.length}</span>
        <span>Responsável</span>
        <span title="Janela de resposta contada a partir da abertura">Prazo</span>
        <span>Etapa</span>
        <span>Próxima ação</span>
        <span />
      </div>

      {regular.map(issue => (
        <ProblemCaseRow
          key={issue.id}
          issue={issue}
          orgMembers={orgMembers}
          isSelected={selectedId === issue.id}
          onOpenWorkspace={onOpenWorkspace}
        />
      ))}

      {regular.length === 0 && (
        <p className="px-3 py-6 text-[12px] text-slate-500">Nenhum caso aberto neste filtro.</p>
      )}

      {closed.length > 0 && (
        <section>
          <button
            type="button"
            onClick={() => setClosedOpen(open => !open)}
            className="flex w-full items-center gap-2 border-y border-slate-200 bg-slate-50 px-3 py-1.5 text-left text-[11px] font-semibold text-slate-600 hover:bg-slate-100 dark:border-slate-700 dark:bg-slate-900/70 dark:text-slate-300 dark:hover:bg-slate-800"
          >
            {closedOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
            Encerrados
            <span className="font-medium text-slate-400">{closed.length}</span>
          </button>
          {closedOpen && closed.map(issue => (
            <ProblemCaseRow
              key={issue.id}
              issue={issue}
              orgMembers={orgMembers}
              isSelected={selectedId === issue.id}
              onOpenWorkspace={onOpenWorkspace}
              muted
            />
          ))}
        </section>
      )}
    </div>
  );
};

function prazoLabel(issue: EnrichedProblemIssue): { text: string; alert: boolean } {
  if (issue.status === 'closed' || issue.status === 'cancelled') {
    return { text: '—', alert: false };
  }
  const windowDays = getIssueSlaDays(issue.severity);
  if (issue.flags.slaOverdue) {
    return {
      text: `${Math.abs(issue.slaRemainingDays)}d além · ${windowDays}d`,
      alert: true,
    };
  }
  return {
    text: `${issue.slaRemainingDays}d · janela ${windowDays}d`,
    alert: issue.flags.slaWarning,
  };
}

function ProblemCaseRow({
  issue,
  orgMembers,
  isSelected,
  onOpenWorkspace,
  muted = false,
}: {
  issue: EnrichedProblemIssue;
  orgMembers: OrgMember[];
  isSelected: boolean;
  onOpenWorkspace: (issue: EnrichedProblemIssue) => void;
  muted?: boolean;
}) {
  const assignee = issue.assigned_to
    ? resolveIssueAssigneeName(issue.assigned_to, orgMembers)
    : 'Não atribuído';
  const prazo = prazoLabel(issue);
  const stage = formatIssueStageLabel(issue);
  const mature = issueHasResolutionMaturity(issue);
  const maturity = mature ? getMaturityLevel(computeIssueProgressLight(issue)) : null;
  const nextAction = getIssueNextAction(issue);
  const settled = issue.status === 'closed' || issue.status === 'cancelled';
  const age = settled ? `idade ${issue.daysOpen}d` : `${issue.daysOpen}d`;

  return (
    <article
      role="button"
      tabIndex={0}
      onClick={() => onOpenWorkspace(issue)}
      onKeyDown={e => { if (e.key === 'Enter') onOpenWorkspace(issue); }}
      className={`${ROW_GRID} relative cursor-pointer border-b border-slate-200 bg-white px-3 py-2 pl-4 transition-colors hover:bg-slate-50 dark:border-slate-800 dark:bg-slate-900 dark:hover:bg-slate-800/70 ${
        isSelected ? 'bg-blue-50/70 dark:bg-blue-950/20' : ''
      } ${muted ? 'opacity-75' : ''}`}
    >
      <span className={`absolute inset-y-0 left-0 w-1 ${SEV_BAR[issue.severity] ?? SEV_BAR.low}`} />
      <div className="min-w-0">
        <h3 title={issue.title} className="truncate text-[13px] font-semibold leading-tight text-slate-900 dark:text-slate-100">
          {issue.title}
        </h3>
        <p className="mt-0.5 truncate text-[11px] text-slate-500">
          {ISSUE_SEVERITY_LABELS[issue.severity]}
          {' · '}
          {ISSUE_TYPE_LABELS[issue.type]}
          {' · '}
          {age}
          {issue.mode === 'structured_problem' ? ' · MASP' : ''}
        </p>
      </div>

      <p className={`truncate text-[12px] ${
        issue.flags.unassigned ? 'font-medium text-amber-700 dark:text-amber-400' : 'text-slate-700 dark:text-slate-200'
      }`}
      >
        {assignee}
      </p>

      <p
        title="Prazo de resposta contado a partir da abertura"
        className={`truncate text-[12px] ${prazo.alert ? 'font-medium text-red-600 dark:text-red-400' : 'text-slate-600 dark:text-slate-300'}`}
      >
        {prazo.text}
      </p>

      <div className="min-w-0">
        <p className="truncate text-[12px] text-slate-800 dark:text-slate-100">{stage}</p>
        {maturity != null && (
          <p className="truncate text-[10px] text-slate-400">Maturidade {maturity}%</p>
        )}
      </div>

      <p title={nextAction.label} className="truncate text-[12px] font-medium text-slate-800 dark:text-slate-100">
        {nextAction.label}
      </p>

      <button
        type="button"
        onClick={e => { e.stopPropagation(); onOpenWorkspace(issue); }}
        className="inline-flex items-center justify-center gap-0.5 rounded border border-slate-200 bg-white px-2 py-1 text-[11px] font-semibold text-slate-700 hover:border-blue-400 hover:text-blue-600 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200"
      >
        Abrir
      </button>
    </article>
  );
}

export default ProblemCaseList;
